import React, { useContext } from 'react';
import { Row, Col, Nav } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { CurrencyContext } from '../../utils/currencyContext';
import { WebPlanContext } from '../../utils/webPlanContext';
import getCurrencySign from '../../utils/getCurrencySign';
import { vpsPlans } from '../../resources/static/index';
import windowsLogo from '../../assets/img/windows.png';
import linuxLogo from '../../assets/img/linux.png';

function PlanColumns({ period }) {
    const { currentCurrency } = useContext(CurrencyContext);
    const { currentWebPlan } = useContext(WebPlanContext);
    const { i18n, t } = useTranslation();
    const getCurrencyCode = (currency) => {
        let currencyCode = 1;

        if (currency === 'usd') {
            currencyCode = 4;
        } else if (currency === 'gbp') {
            currencyCode = 5;
        }

        return currencyCode;
    };
    const getPeriodDiscount = (per) => {
        let discount = null;

        if (per === 6) {
            discount = '10%';
        } else if (per === 12) {
            discount = '15%';
        }

        return discount;
    };
    const getBillingCycle = (currentPlan) => {
        let currencyCode = 'annually';

        if (currentPlan === 'two') {
            currencyCode = 'biennially';
        } else if (currentPlan === 'three') {
            currencyCode = 'triennially';
        }

        return currencyCode;
    };
    const roundToTwo = (num) => parseFloat(num.toFixed(2));
    const sign = getCurrencySign(currentCurrency);
    const dataCurrency = vpsPlans[currentCurrency];
    const data = dataCurrency[`month_${period}`];
    const isNotFirstPeriod = period > 1;
    const yearCoefficient = period;
    const currentLanguage = i18n.language;
    const isFR = currentLanguage === 'fr';

    return (
        <Row>
            <Col lg={4} md={12} xs={12}>
                <div className="plan-card">
                    <h2 className="plan-title">
                        VPS <span>Start</span>
                    </h2>
                    <p className="plan-text">{t('vps-page.plans-column.text')}</p>
                    <div className="plan-price">
                        <p className="price-info">
                            <span className="price-home">
                                {data.vps_start}
                                <sup>{sign}</sup>
                            </span>
                            /{t('titles.month')}
                        </p>
                        <p className="price-year">
                            {isNotFirstPeriod && (
                                <del>
                                    {roundToTwo(dataCurrency.month_1.vps_start * yearCoefficient)}
                                    {sign}
                                </del>
                            )}
                            &nbsp;
                            <strong>
                                {isNotFirstPeriod
                                    ? roundToTwo(data.vps_start * yearCoefficient)
                                    : roundToTwo(dataCurrency.month_1.vps_start * yearCoefficient)}
                                {sign}
                            </strong>
                            &nbsp;{t('titles.for')} {period}{' '}
                            {period > 1 ? t('titles.months') : t('titles.month')}
                        </p>
                    </div>
                    {isNotFirstPeriod && (
                        <div className="price-save">
                            {t('titles.save')} {getPeriodDiscount(period)}
                        </div>
                    )}
                    <div className="price-content">
                        <img src={windowsLogo} alt="Windows" />
                        <img src={linuxLogo} alt="Linux" />
                    </div>
                    <div className="plan-feature">
                        <ul>
                            <li className="plan-count">
                                <p>
                                    <strong>50{t('web-hosting-page.plans-column.gb')}</strong>{' '}
                                    {t('vps-page.plans-column.property-1')}
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>1 CPU</strong> @4.9GHz
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>2{t('web-hosting-page.plans-column.gb')}</strong> RAM
                                    DDR4
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>200 Mb/s</strong>{' '}
                                    {t('vps-page.plans-column.property-2')}
                                </p>
                            </li>
                        </ul>
                    </div>
                    <Nav className="common-nav-button">
                        <Nav.Link
                            className="navbar-brand"
                            href={`https://obambu.com/order/?pid=176&language=${isFR ? 'french' : 'english'}&currency=${getCurrencyCode(currentCurrency)}&billingcycle=${getBillingCycle(currentWebPlan)}`}
                            rel="noopener noreferrer"
                        >
                            {t('buttons.order-now')}
                        </Nav.Link>
                    </Nav>
                </div>
            </Col>
            <Col lg={4} md={12} xs={12}>
                <div className="plan-card active">
                    <div className="badge-active">{t('vps-page.vps-plans-badge')}</div>
                    <h2 className="plan-title">
                        VPS <span>Grow</span>
                    </h2>
                    <p className="plan-text">{t('vps-page.plans-column.text-2')}</p>
                    <div className="plan-price">
                        <p className="price-info">
                            <span className="price-home">
                                {data.vps_grow}
                                <sup>{sign}</sup>
                            </span>
                            /{t('titles.month')}
                        </p>
                        <p className="price-year">
                            {isNotFirstPeriod && (
                                <del>
                                    {roundToTwo(dataCurrency.month_1.vps_grow * yearCoefficient)}
                                    {sign}
                                </del>
                            )}
                            &nbsp;
                            <strong>
                                {isNotFirstPeriod
                                    ? roundToTwo(data.vps_grow * yearCoefficient)
                                    : roundToTwo(dataCurrency.month_1.vps_grow * yearCoefficient)}
                                {sign}
                            </strong>
                            &nbsp;{t('titles.for')} {period}{' '}
                            {period > 1 ? t('titles.months') : t('titles.month')}
                        </p>
                    </div>
                    {isNotFirstPeriod && (
                        <div className="price-save">
                            {t('titles.save')} {getPeriodDiscount(period)}
                        </div>
                    )}
                    <div className="price-content">
                        <img src={windowsLogo} alt="Windows" />
                        <img src={linuxLogo} alt="Linux" />
                    </div>
                    <div className="plan-feature">
                        <ul>
                            <li className="plan-count">
                                <p>
                                    <strong>100{t('web-hosting-page.plans-column.gb')}</strong>{' '}
                                    {t('vps-page.plans-column.property-1')}
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>2 CPU</strong> @4.9GHz
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>4{t('web-hosting-page.plans-column.gb')}</strong> RAM
                                    DDR4
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>500 Mb/s</strong>{' '}
                                    {t('vps-page.plans-column.property-2')}
                                </p>
                            </li>
                        </ul>
                    </div>
                    <Nav className="common-nav-button">
                        <Nav.Link
                            className="navbar-brand"
                            href={`https://obambu.com/order/?pid=177&language=${isFR ? 'french' : 'english'}&currency=${getCurrencyCode(currentCurrency)}&billingcycle=${getBillingCycle(currentWebPlan)}`}
                            rel="noopener noreferrer"
                        >
                            {t('buttons.order-now')}
                        </Nav.Link>
                    </Nav>
                </div>
            </Col>
            <Col lg={4} md={12} xs={12}>
                <div className="plan-card">
                    <h2 className="plan-title">
                        VPS <span>Advance</span>
                    </h2>
                    <p className="plan-text">{t('vps-page.plans-column.text-3')}</p>
                    <div className="plan-price">
                        <p className="price-info">
                            <span className="price-home">
                                {data.vps_advance}
                                <sup>{sign}</sup>
                            </span>
                            /{t('titles.month')}
                        </p>
                        <p className="price-year">
                            {isNotFirstPeriod && (
                                <del>
                                    {roundToTwo(dataCurrency.month_1.vps_advance * yearCoefficient)}
                                    {sign}
                                </del>
                            )}
                            &nbsp;
                            <strong>
                                {isNotFirstPeriod
                                    ? roundToTwo(data.vps_advance * yearCoefficient)
                                    : roundToTwo(
                                          dataCurrency.month_1.vps_advance * yearCoefficient
                                      )}
                                {sign}
                            </strong>
                            &nbsp;{t('titles.for')} {period}{' '}
                            {period > 1 ? t('titles.months') : t('titles.month')}
                        </p>
                    </div>
                    {isNotFirstPeriod && (
                        <div className="price-save">
                            {t('titles.save')} {getPeriodDiscount(period)}
                        </div>
                    )}
                    <div className="price-content">
                        <img src={windowsLogo} alt="Windows" />
                        <img src={linuxLogo} alt="Linux" />
                    </div>
                    <div className="plan-feature">
                        <ul>
                            <li className="plan-count">
                                <p>
                                    <strong>200{t('web-hosting-page.plans-column.gb')}</strong>{' '}
                                    {t('vps-page.plans-column.property-1')}
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>4 CPU</strong> @4.9GHz
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>8{t('web-hosting-page.plans-column.gb')}</strong> RAM
                                    DDR4
                                </p>
                            </li>
                            <li>
                                <p>
                                    <strong>500 Mb/s</strong>{' '}
                                    {t('vps-page.plans-column.property-2')}
                                </p>
                            </li>
                        </ul>
                    </div>
                    <Nav className="common-nav-button">
                        <Nav.Link
                            className="navbar-brand"
                            href={`https://obambu.com/order/?pid=178&language=${isFR ? 'french' : 'english'}&currency=${getCurrencyCode(currentCurrency)}&billingcycle=${getBillingCycle(currentWebPlan)}`}
                            rel="noopener noreferrer"
                        >
                            {t('buttons.order-now')}
                        </Nav.Link>
                    </Nav>
                </div>
            </Col>
        </Row>
    );
}

PlanColumns.propTypes = {
    period: PropTypes.number
};

PlanColumns.defaultProps = {
    period: 1
};

export default PlanColumns;
